import {useState} from 'react';
import {useParams, Link} from 'react-router-dom';
import {format} from 'date-fns';
import {
  ArrowLeft,
  Loader2,
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Package,
} from 'lucide-react';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Badge} from '@common-web/components/ui/badge';
import {Button} from '@common-web/components/ui/button';
import {DataTable, useDataTable, type Column} from '@common-web/components/data-table';
import {PageHeader, PageHeaderAction} from '@common-web/components/page-header';
import {Alert, AlertDescription} from '@common-web/components/ui/alert';
import {trpc, RouterOutput} from '@common-web/lib/trpc';
import {useToast} from '@common-web/hooks/use-toast';

type BatchItem = RouterOutput['internal']['invoice']['getGenerationBatch']['items'][number];
type StatusFilter = 'all' | 'confirmed' | 'missing' | 'failed' | 'pending';

type BatchItemFilters = {statusFilter: string};

const formatDate = (date: Date | null | undefined) => {
  if (!date) return 'N/A';
  try {
    return format(new Date(date), 'MM/dd/yyyy');
  } catch {
    return 'N/A';
  }
};

const formatDateTime = (date: Date | null | undefined) => {
  if (!date) return 'N/A';
  try {
    return format(new Date(date), 'MM/dd/yyyy h:mm a');
  } catch {
    return 'N/A';
  }
};

function getItemStatusBadge(status: string) {
  switch (status) {
    case 'confirmed':
      return (
        <Badge className='bg-emerald text-white border-0 font-medium'>
          <CheckCircle className='h-3 w-3 mr-1' />
          Confirmed
        </Badge>
      );
    case 'missing':
      return (
        <Badge className='bg-amber text-white border-0 font-medium'>
          <AlertTriangle className='h-3 w-3 mr-1' />
          Missing
        </Badge>
      );
    case 'failed':
      return (
        <Badge className='bg-red-600 text-white border-0 font-medium'>
          <XCircle className='h-3 w-3 mr-1' />
          Failed
        </Badge>
      );
    case 'pending':
      return (
        <Badge className='bg-brand-gray text-white border-0 font-medium'>
          <Clock className='h-3 w-3 mr-1' />
          Pending
        </Badge>
      );
    default:
      return (
        <Badge className='bg-brand-gray/10 text-brand-gray border-brand-gray/20'>{status}</Badge>
      );
  }
}

export function GenerationBatchDetail() {
  const {batchId} = useParams<{batchId: string}>();
  const {toast} = useToast();
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

  const table = useDataTable<BatchItemFilters>({
    defaultPageSize: 50,
    defaultFilters: {statusFilter: ''},
  });

  const utils = trpc.useUtils();

  const {data, isLoading, isFetching, error} = trpc.internal.invoice.getGenerationBatch.useQuery(
    {
      batchId: batchId!,
      page: table.page,
      pageSize: table.pageSize,
      statusFilter: (table.filterValues.statusFilter || 'all') as StatusFilter,
    },
    {enabled: !!batchId},
  );

  const retriggerMutation = trpc.internal.invoice.retriggerItems.useMutation({
    onSuccess: (result) => {
      toast({title: `Retriggered ${result.retriggeredCount} items`});
      setSelectedItems(new Set());
      utils.internal.invoice.getGenerationBatch.invalidate();
    },
    onError: (err) => {
      toast({title: 'Retrigger failed', description: err.message, variant: 'destructive'});
    },
  });

  const regenerateBatchMutation = trpc.internal.invoice.regenerateBatch.useMutation({
    onSuccess: (result) => {
      const desc = result.failedCount > 0 ? `${result.failedCount} failed` : undefined;
      toast({
        title: `Regenerated ${result.regeneratedCount} invoices`,
        description: desc,
        variant: result.failedCount > 0 ? 'destructive' : 'default',
      });
      utils.internal.invoice.getGenerationBatch.invalidate();
    },
    onError: (err) => {
      toast({title: 'Regeneration failed', description: err.message, variant: 'destructive'});
    },
  });

  const toggleSelectItem = (itemId: string) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const selectAllRetriggerable = () => {
    if (!data) return;
    const ids = data.items
      .filter((i) => i.status === 'missing' || i.status === 'failed')
      .map((i) => i.id);
    setSelectedItems(new Set(ids));
  };

  const handleRetrigger = () => {
    if (selectedItems.size === 0) return;
    retriggerMutation.mutate({itemIds: Array.from(selectedItems)});
  };

  const columns: Column<BatchItem>[] = [
    {
      id: 'select',
      header: '',
      render: (item) => {
        const canRetrigger = item.status === 'missing' || item.status === 'failed';
        return canRetrigger ? (
          <input
            type='checkbox'
            checked={selectedItems.has(item.id)}
            onChange={() => toggleSelectItem(item.id)}
            className='h-4 w-4 rounded-sm border-gray-300 text-brand-blue focus:ring-brand-blue'
          />
        ) : null;
      },
    },
    {
      id: 'group',
      header: 'Group',
      render: (item) => (
        <div>
          <div className='font-medium text-brand-navy'>{item.groupName}</div>
          <div className='text-xs text-brand-gray font-mono'>{item.vbaGroupId}</div>
        </div>
      ),
    },
    {
      id: 'client',
      header: 'Client',
      render: (item) => <span className='text-sm text-brand-gray'>{item.clientName || '-'}</span>,
    },
    {
      id: 'period',
      header: 'Billing Period',
      render: (item) => (
        <div className='text-sm'>
          {formatDate(item.billFrom)} - {formatDate(item.billThru)}
        </div>
      ),
    },
    {
      id: 'invoiceKey',
      header: 'Invoice Key',
      render: (item) =>
        item.returnedInvoiceKey ? (
          <Link
            to={`/invoices/${item.returnedInvoiceKey}`}
            className='font-mono text-sm text-brand-blue hover:text-brand-blue-dark hover:underline'
          >
            {item.returnedInvoiceKey}
          </Link>
        ) : (
          <span className='text-sm text-brand-gray'>-</span>
        ),
    },
    {
      id: 'status',
      header: 'Status',
      render: (item) => getItemStatusBadge(item.status),
    },
    {
      id: 'error',
      header: 'Error',
      render: (item) =>
        item.vbaError ? (
          <span className='text-xs text-red-600 truncate max-w-[200px] block' title={item.vbaError}>
            {item.vbaError}
          </span>
        ) : null,
    },
    {
      id: 'submitted',
      header: 'Submitted',
      render: (item) => (
        <span className='text-xs text-brand-gray'>{formatDateTime(item.submittedAt)}</span>
      ),
    },
  ];

  if (!batchId) {
    return <div>Batch ID not provided</div>;
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        {/* Header */}
        <PageHeader
          title={`Batch: ${data?.batch.targetMonth ?? '...'}`}
          description={data?.batch.note ?? 'Invoice generation batch detail'}
          icon={<Package className='h-6 w-6' />}
          isFetching={isFetching}
          isLoading={isLoading}
        >
          <PageHeaderAction asChild tone='secondary'>
            <Link to='/invoices?tab=generation'>
              <ArrowLeft className='h-4 w-4 mr-2' />
              Back to Generation Runs
            </Link>
          </PageHeaderAction>
        </PageHeader>

        <div className='px-8 py-6 max-w-7xl mx-auto w-full space-y-6'>
          {data && (
            <>
              {/* Batch info */}
              <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
                <Card className='border-gray-200 shadow-sm'>
                  <CardContent className='p-4'>
                    <div className='text-xs font-semibold text-brand-gray uppercase tracking-wider mb-1'>
                      Invoice Date
                    </div>
                    <div className='text-lg font-bold text-brand-navy'>
                      {formatDate(data.batch.invoiceDate)}
                    </div>
                  </CardContent>
                </Card>
                <Card className='border-gray-200 shadow-sm'>
                  <CardContent className='p-4'>
                    <div className='text-xs font-semibold text-brand-gray uppercase tracking-wider mb-1'>
                      Created
                    </div>
                    <div className='text-lg font-bold text-brand-navy'>
                      {formatDateTime(data.batch.createdAt)}
                    </div>
                  </CardContent>
                </Card>
                <Card className='border-gray-200 shadow-sm'>
                  <CardContent className='p-4'>
                    <div className='text-xs font-semibold text-brand-gray uppercase tracking-wider mb-1'>
                      Total Items
                    </div>
                    <div className='text-lg font-bold text-brand-navy'>{data.stats.total}</div>
                  </CardContent>
                </Card>
                <Card className='border-gray-200 shadow-sm'>
                  <CardContent className='p-4'>
                    <div className='text-xs font-semibold text-brand-gray uppercase tracking-wider mb-1'>
                      Target Month
                    </div>
                    <div className='text-lg font-bold text-brand-navy'>
                      {data.batch.targetMonth}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Status summary cards */}
              <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
                <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
                  <CardHeader className='pb-3'>
                    <CardTitle className='text-sm font-medium text-brand-gray flex items-center justify-between'>
                      Confirmed
                      <CheckCircle className='h-5 w-5 text-emerald' />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className='text-3xl font-bold text-emerald'>{data.stats.confirmed}</div>
                    <p className='text-xs text-brand-gray mt-1'>Verified in VBA</p>
                  </CardContent>
                </Card>

                <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
                  <CardHeader className='pb-3'>
                    <CardTitle className='text-sm font-medium text-brand-gray flex items-center justify-between'>
                      Missing
                      <AlertTriangle className='h-5 w-5 text-amber' />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className='text-3xl font-bold text-amber'>{data.stats.missing}</div>
                    <p className='text-xs text-brand-gray mt-1'>Key returned, not in VBA</p>
                  </CardContent>
                </Card>

                <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
                  <CardHeader className='pb-3'>
                    <CardTitle className='text-sm font-medium text-brand-gray flex items-center justify-between'>
                      Failed
                      <XCircle className='h-5 w-5 text-red-500' />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className='text-3xl font-bold text-red-600'>{data.stats.failed}</div>
                    <p className='text-xs text-brand-gray mt-1'>VBA error</p>
                  </CardContent>
                </Card>

                <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
                  <CardHeader className='pb-3'>
                    <CardTitle className='text-sm font-medium text-brand-gray flex items-center justify-between'>
                      Pending
                      <Clock className='h-5 w-5 text-brand-gray' />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className='text-3xl font-bold text-brand-gray'>{data.stats.pending}</div>
                    <p className='text-xs text-brand-gray mt-1'>Not yet submitted</p>
                  </CardContent>
                </Card>
              </div>

              {/* Actions */}
              <Card className='border-gray-200 shadow-sm'>
                <CardContent className='p-6'>
                  <div className='flex items-center gap-4 flex-wrap'>
                    <Button
                      size='sm'
                      onClick={() => regenerateBatchMutation.mutate({batchId: batchId!})}
                      disabled={regenerateBatchMutation.isPending}
                      className='bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold shadow-sm transition-colors'
                    >
                      {regenerateBatchMutation.isPending ? (
                        <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                      ) : (
                        <RefreshCw className='h-4 w-4 mr-2' />
                      )}
                      Regenerate All
                    </Button>

                    {(data.stats.missing > 0 || data.stats.failed > 0) && (
                      <div className='flex items-center gap-2'>
                        <Button
                          variant='outline'
                          size='sm'
                          onClick={selectAllRetriggerable}
                          className='border-gray-200'
                        >
                          Select All Retriggerable
                        </Button>
                        <Button
                          size='sm'
                          onClick={handleRetrigger}
                          disabled={selectedItems.size === 0 || retriggerMutation.isPending}
                          className='bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold shadow-sm transition-colors'
                        >
                          {retriggerMutation.isPending ? (
                            <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                          ) : (
                            <RefreshCw className='h-4 w-4 mr-2' />
                          )}
                          Retrigger ({selectedItems.size})
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Items Table */}
          <DataTable<BatchItem, BatchItemFilters>
            table={table}
            columns={columns}
            data={data?.items}
            total={data?.totalCount}
            totalPages={data?.totalPages}
            loading={isLoading}
            error={error}
            emptyMessage='No items match the current filter'
            title='Items'
            entity={{singular: 'item', plural: 'items'}}
            filters={[
              {
                key: 'statusFilter',
                label: 'Statuses',
                type: 'select',
                placeholder: 'Filter by status',
                options: [
                  {value: 'confirmed', label: 'Confirmed'},
                  {value: 'missing', label: 'Missing'},
                  {value: 'failed', label: 'Failed'},
                  {value: 'pending', label: 'Pending'},
                ],
              },
            ]}
            rowKey={(item) => item.id}
          />
        </div>
      </div>
    </div>
  );
}
